import type { DropdownOption } from '~/flow/types';
import type { AggregationType } from 'flow/store/engine/constants';
import { AGGREGATION_TYPE_LABELS } from './constants';
import type {
  AggregationColumn,
  AggregationColumnOption,
  AggregationFilterOption,
  AggregationFilterUiDataType,
} from '~/dashboard/views/settings/aggregations/types';
import type { AggregationFilter } from 'flow/store/engine/types';
import type { AggregationSourceData, AggregationValidationErrors, OptionGroup } from './types';

/**
 * Returns the UI-facing key for the selected aggregation column.
 * For PA columns this is the token (matching option.value);
 * for schema columns it's the column slug itself.
 */
export const getColumnKey = (
  source: Pick<AggregationFilter, 'columnSlug' | 'publishedAttributeMetadata'>,
): string => source.publishedAttributeMetadata?.publishedAttributeToken
  ?? source.columnSlug;

/** Resolves the effective ui_data_type, unwrapping published_attribute to its sub-type. */
export const resolveDataType = (
  filterOption?: AggregationFilterOption,
): Exclude<AggregationFilterUiDataType, 'published_attribute'> => {
  if (filterOption?.ui_data_type === 'published_attribute') {
    return filterOption.published_attribute_ui_data_type ?? 'string';
  }
  return (filterOption?.ui_data_type ?? 'string') as Exclude<
  AggregationFilterUiDataType, 'published_attribute'
  >;
};

export function buildOptionGroups<T extends AggregationFilterOption>(
  options: T[],
  objectDisplayName: string,
): OptionGroup<T>[] {
  if (options.length === 0) return [];
  const schemaOptions: T[] = [];
  const publishedAttrOptions: T[] = [];

  for (const option of options) {
    if (option.ui_data_type === 'published_attribute') {
      publishedAttrOptions.push(option);
    } else {
      schemaOptions.push(option);
    }
  }

  const groups: OptionGroup<T>[] = [];

  if (schemaOptions.length > 0) {
    groups.push({
      label: `${objectDisplayName} Schema`,
      value: null,
      options: schemaOptions,
    });
  }

  if (publishedAttrOptions.length > 0) {
    groups.push({
      label: `${objectDisplayName} Published Attributes`,
      value: null,
      options: publishedAttrOptions,
    });
  }

  return groups;
}

function findColumnByKey(
  columnOptions: AggregationColumnOption[],
  key: string,
): AggregationColumnOption | undefined {
  return columnOptions.find(col => col.value === key);
}

/**
 * Derives the aggregation-type dropdown options from the selected column.
 * Returns an empty array when no column is selected or the column has no
 * allowed aggregation types.
 */
export function getAggregationTypeOptions(
  columnOptions: AggregationColumnOption[],
  selectedColumnKey: string,
): DropdownOption[] {
  const selected = findColumnByKey(columnOptions, selectedColumnKey);
  if (!selected) return [];
  return selected.aggregations
    .filter(type => type in AGGREGATION_TYPE_LABELS)
    .map(type => ({
      label: AGGREGATION_TYPE_LABELS[type],
      value: type,
    }));
}

/**
 * Returns the current aggregationType if it is still valid for the new column,
 * otherwise falls back to the first allowed type for that column.
 */
export function reconcileAggregationType(
  columnOptions: AggregationColumnOption[],
  newColumnKey: string,
  currentAggregationType: AggregationType,
): AggregationType {
  const col = findColumnByKey(columnOptions, newColumnKey);
  if (!col) return currentAggregationType;
  const supported = col.aggregations.filter(type => type in AGGREGATION_TYPE_LABELS);
  if (!supported.length) return currentAggregationType;
  return supported.includes(currentAggregationType)
    ? currentAggregationType
    : supported[0];
}

function expandPublishedAttributes(c: AggregationColumn) {
  if (c.ui_data_type !== 'published_attribute') return null;
  if (!c.published_attribute_metadata?.published_attributes) return [];

  return c.published_attribute_metadata.published_attributes.map(pa => ({
    ui_data_type: c.ui_data_type,
    column_slug: c.column_slug,
    value: pa.published_attribute_token,
    label: pa.published_attribute_name
      || pa.published_attribute_evaluation_payload_path,
    published_attribute_description: pa.published_attribute_description,
    published_attribute_ui_data_type: pa.published_attribute_ui_data_type,
    published_attribute_name: pa.published_attribute_name,
    data_type: pa.data_type,
    contains_pii: pa.contains_pii,
    aggregations: pa.aggregations,
    possible_values: pa.possible_values,
  }));
}

export function toColumnOptions(
  columns: AggregationColumn[],
): AggregationColumnOption[] {
  return columns
    .filter(col => col.user_aggregable)
    .flatMap(column => (
      expandPublishedAttributes(column) ?? [{
        label: column.display_name,
        value: column.column_slug,
        aggregations: column.aggregations,
        ui_data_type: column.ui_data_type,
      }]
    ))
    .filter(option => option.aggregations.some(
      type => type in AGGREGATION_TYPE_LABELS,
    ));
}

export function toFilterOptions(
  columns: AggregationColumn[],
): AggregationFilterOption[] {
  return columns
    .filter(col => col.user_filterable)
    .flatMap(column => (
      expandPublishedAttributes(column) ?? [{
        label: column.display_name,
        value: column.column_slug,
        ui_data_type: column.ui_data_type,
      }]
    ));
}

function hasStaticValue(filter: AggregationFilter): boolean {
  const { staticValue } = filter;
  if (staticValue == null) return false;
  if (typeof staticValue === 'string') return staticValue !== '';
  if (Array.isArray(staticValue)) return staticValue.length > 0;
  return typeof staticValue === 'object';
}

function isFilterComplete(filter: AggregationFilter): boolean {
  return !!filter.dynamicColumnSlug || hasStaticValue(filter);
}

export function getValidationErrors(
  sourceData: AggregationSourceData,
): AggregationValidationErrors {
  const errors: AggregationValidationErrors = {};
  if (!sourceData.name) errors.name = 'Name is required';
  if (!sourceData.description?.trim()) errors.description = 'Description is required';
  return errors;
}

/**
 * Returns true when the aggregation editor should prevent saving.
 * Checks non-validation fields: aggregation column/type, scope, timeframe,
 * and that every filter has either a static value or a dynamic column slug.
 * Name and description are handled separately by getValidationErrors.
 */
export function getIsSaveDisabled(sourceData: AggregationSourceData): boolean {
  const {
    aggregation: { columnSlug, aggregationType },
    scope,
    timeframeUnit,
    timeframeIntervalEnd,
    timeframeIntervalStart,
  } = sourceData;

  const hasEmptyBaseField = !columnSlug
    || !aggregationType
    || !scope
    || !timeframeUnit
    || timeframeIntervalEnd == null
    || timeframeIntervalStart == null;

  if (hasEmptyBaseField) return true;

  if (sourceData.filters.some(f => !isFilterComplete(f))) return true;

  return false;
}

